import { defaultColors } from '@/lib/colors'
import { setGlobalCSSVar } from '@/lib/utils'
import { useEffect, useLayoutEffect, useState } from 'react'
import { z } from 'zod'

interface ChatWidgetSettings {
  onSuggestedQuestion: (question: string) => void
}

const configDataSchema = z.object({
  primaryColor: z.string().optional(),
  backgroundColor: z.string().optional(),
  textColor: z.string().optional(),
  hideBranding: z.boolean().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  greeting: z.string().optional(),
  suggestedQuestions: z.array(z.string()).optional(),
  profileImage: z.string().optional(),
  isAvailable: z.boolean().optional(),
})

const configMessageSchema = z.object({
  messageType: z.literal('config'),
  data: configDataSchema,
})

const suggestedQuestionMessageSchema = z.object({
  messageType: z.literal('suggestedQuestion'),
  data: z.string(),
})

const messageSchema = z.discriminatedUnion('messageType', [configMessageSchema, suggestedQuestionMessageSchema])

export interface HandleIFrameMessagesConfig {
  primaryColor: string
  backgroundColor: string
  textColor: string
  hideBranding?: boolean
  name?: string
  description?: string
  greeting?: string
  suggestedQuestions?: string[]
  profileImage?: string
  isAvailable?: boolean
}

export function useHandleIFrameMessages({ onSuggestedQuestion }: ChatWidgetSettings) {
  const [config, setConfig] = useState<HandleIFrameMessagesConfig>()

  useLayoutEffect(() => {
    if (!config) return

    if (config.primaryColor) {
      setGlobalCSSVar('--sensay-widget-primary-color', config.primaryColor)
    }
    if (config.backgroundColor) {
      setGlobalCSSVar('--sensay-widget-background-color', config.backgroundColor)
    }
    if (config.textColor) {
      setGlobalCSSVar('--sensay-widget-foreground-color', config.textColor)
    }
  }, [config])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const payload = event.data

      const parsed = messageSchema.safeParse(payload)

      if (!parsed.success) return

      const { messageType, data } = parsed.data

      switch (messageType) {
        case 'config':
          setConfig((prevConfig) => ({
            ...prevConfig,
            primaryColor: data.primaryColor ?? defaultColors.primaryColor,
            backgroundColor: data.backgroundColor ?? defaultColors.backgroundColor,
            textColor: data.textColor ?? defaultColors.textColor,
            hideBranding: data.hideBranding,
            name: data.name ?? '',
            description: data.description,
            greeting: data.greeting,
            suggestedQuestions: data.suggestedQuestions,
            profileImage: data.profileImage,
            isAvailable: data.isAvailable,
          }))
          return
        case 'suggestedQuestion':
          onSuggestedQuestion(data)
          return
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [onSuggestedQuestion])

  return { config }
}
